import { type RefObject, useRef, useState } from 'react'
import { toast } from 'sonner'
import type { ClearDictionaryOutcome } from '@/api/types/dictionary'
import { notifyDictionaryUpdateFailed } from '../handlers/notify-dictionary-change-failed'

interface UseClearAllOptions {
  onClearAll: () => Promise<ClearDictionaryOutcome>
  /** Receives the screen-reader text once the dictionary is cleared. */
  onAnnounce: (message: string) => void
  /** Takes focus after a full clear, since Clear All leaves with the list. */
  inputRef: RefObject<HTMLInputElement | null>
  /** Takes focus when the dialog closes and words are still listed. */
  clearAllButtonRef: RefObject<HTMLButtonElement | null>
}

/**
 * Clear All: the confirmation dialog and the clear itself. The dialog stays open, and can't be
 * dismissed, until the backend has answered; the list only changes then, so a failed clear never
 * looks like an empty dictionary. Each ending gets its toast from Figma "Dictionary Failure
 * Notifications": cleared, partly cleared (the list shows what is left), or failed (nothing
 * changed, so Clear All is still there to try again).
 */
export function useClearAll({
  onClearAll,
  onAnnounce,
  inputRef,
  clearAllButtonRef,
}: UseClearAllOptions) {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isClearing, setIsClearing] = useState(false)
  // State lags a render behind, so a second press in the same tick reads this ref instead.
  const isClearingRef = useRef(false)
  // Set on a full clear: Clear All unmounts with the list, so focus goes to the Add-word input.
  const focusInputOnCloseRef = useRef(false)

  const setDialogOpen = (open: boolean) => {
    if (!isClearingRef.current) setIsDialogOpen(open)
  }

  const confirm = async () => {
    if (isClearingRef.current) return

    isClearingRef.current = true
    setIsClearing(true)

    let outcome: ClearDictionaryOutcome

    try {
      outcome = await onClearAll()
    } finally {
      isClearingRef.current = false
      setIsClearing(false)
    }

    if (outcome.status === 'cleared') {
      const count = outcome.removedCount

      focusInputOnCloseRef.current = true
      onAnnounce('Custom dictionary cleared.')
      toast.success('Custom words cleared.', {
        description: `${count} ${count === 1 ? 'word was' : 'words were'} removed from the dictionary.`,
      })
    } else if (outcome.status === 'incomplete') {
      toast.error('Not all custom words were cleared. Please review.')
    } else {
      notifyDictionaryUpdateFailed()
    }

    setIsDialogOpen(false)
  }

  // The dialog has no DialogTrigger, so Radix has nothing to return focus to; pick it here.
  const handleCloseAutoFocus = (event: Event) => {
    event.preventDefault()
    const target = focusInputOnCloseRef.current
      ? inputRef.current
      : (clearAllButtonRef.current ?? inputRef.current)

    focusInputOnCloseRef.current = false
    target?.focus()
  }

  return { isDialogOpen, setDialogOpen, isClearing, confirm, handleCloseAutoFocus }
}
